import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import ControlPropertyItem from './ControlPropertyItem';
import Loader from '../../../../../LoadingIndicator/LoadingIndicator';
import {getControlById} from '../../../../../../../actions/Control/controlaction';
import {labels} from '../../../../../../../util/utils';
import Utility from '../../../../../../../util/utilityfunctions';
import {useEngagementId, useCanvasFormMetadata} from '../../../../../../../util/customHooks';
import {useSmartEvidenceSourceId} from '../../../../../../../util/Ares/customHooks';
import ControlPropertiesButton from './ControlPropertiesButton';
import CreateEditControlModalWrapper from '../../../../../../../components/Common/EntityCrud/CreateEditControlModalWrapper';

export default function ControlProperties({bodyId}) {
	const dispatch = useDispatch();
	const engagementId = useEngagementId();
	const documentId = Utility.getDocumentIdFromUrl(window);
	const controlId = useSmartEvidenceSourceId(documentId);
	const controls = useSelector((store) => store.bodiesSpecial.controls?.byId);

	const [control, setControl] = useState(null);
	const [fetching, setFetching] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [showLoader, setShowLoader] = useState(true);
	const [controlTestingIndex, setControlTestingIndex] = useState(0);
	const [controlDesignEffectivenessIndex, setControlDesignEffectivenessIndex] = useState(0);
	const {isShared, isTargetEngagement} = useCanvasFormMetadata();

	const fetchData = async (withItApplications = false) => {
		try {
			if (controlId) {
				let controlExpand = withItApplications ? 'itapplications' : '';
				await dispatch(getControlById(engagementId, controlId, documentId, controlExpand));
			}
		} finally {
			setShowLoader(false);
		}
	};

	useEffect(() => {
		fetchData();
	}, [controlId]);

	useEffect(() => {
		if (controls && controlId && controls[controlId]) {
			setControl(controls[controlId]);
		}
	}, [controls]);

	useEffect(() => {
		// Testing
		if ((control?.hasControlTesting ?? null) !== null) {
			setControlTestingIndex(control.hasControlTesting ? 1 : 2);
		} else {
			setControlTestingIndex(0);
		}

		// Operating effectiveness
		if ((control?.isEffective ?? null) !== null) {
			setControlDesignEffectivenessIndex(control.isEffective ? 1 : 2);
		} else {
			setControlDesignEffectivenessIndex(0);
		}

	}, [control]);

	const handleClickEditControlProperties = () => {
		if (!control.itApplications || control.itApplications.length === 0) {
			setFetching(true);

			fetchData(true).finally(() => {
				setShowModal(true);
				setFetching(false);
			});
		} else {
			setShowModal(true);
		}
	};

	return (
		<Wrapper className="ControlPropertiesWraper">
			{showLoader ? (
				<Loader />
			) : control && Object.keys(control).length > 0 ? (
				<>
					<ControlDescription>
						<StyledHeader>
							<section className="control-name-label">{labels.controlName}</section>
							<section className="actions">
								<ControlPropertiesButton
									disabled={fetching || (isShared && isTargetEngagement)}
									handleOnClick={handleClickEditControlProperties}
								/>
							</section>
						</StyledHeader>
						<StyledControlName>{control.controlName}</StyledControlName>
					</ControlDescription>
					<ControlDetails>
						<section>
							<ControlPropertyItem
								isNotSelected={!control.controlTypeId}
								label={labels.controlTypeLabel}
								content={labels.controlTypeEnumLabel[control.controlTypeId || 0].controlTypeName}
							/>
							<ControlPropertyItem
								isNotSelected={!control.controlFrequencyTypeId}
								label={labels.frequency}
								content={labels.controlFrequencyType[control.controlFrequencyTypeId || 0].controlFrequencyTypeName}
							/>
							<ControlPropertyItem
								isNotSelected={!controlTestingIndex}
								label={labels.testingTitle}
								content={labels.controlTesting[controlTestingIndex].description}
							/>
						</section>
						<section>
							<ControlPropertyItem
								label={labels.controlLowerRisk}
								content={control.isLowerRisk ? labels.yes : labels.no}
							/>
							<ControlPropertyItem
								isNotSelected={!control.controlDesignEffectivenessTypeId}
								label={labels.formsBodyDesignEffectiveness}
								content={labels.controlDesignEffectiveness[control.controlDesignEffectivenessTypeId || 0].description}
							/>
							<ControlPropertyItem
								isNotSelected={!controlDesignEffectivenessIndex}
								label={labels.controlOpertaingEffectiveness}
								content={labels.controlOperationEffectiveness[controlDesignEffectivenessIndex || 0].description}
							/>
						</section>
					</ControlDetails>

					{showModal && (
						<CreateEditControlModalWrapper
							isEdit
							bodyId={bodyId}
							documentId={documentId}
							showModal={showModal}
							setShowModal={setShowModal}
							controlId={Number(control.id)}
							viewOnly={false}
						/>
					)}
				</>
			) : (
				<StyledNoControlAvailable>{labels.noControlAvailable}</StyledNoControlAvailable>
			)}
		</Wrapper>
	);
}

const Wrapper = styled.section`
	display: flex;
	flex-direction: column;
	justify-content: center;
	min-height: 16rem;
	border: var(--px-1) solid var(--neutrals-200);
	border-radius: var(--px-8);
	overflow: hidden;
	padding: var(--px-16);
	background-color: var(--white);

	> section {
		display: flex;
		flex-direction: column;
	}
`;

const StyledNoControlAvailable = styled.section`
	font-size: var(--px-14);
	color: var(--neutrals-900);
	text-align: center;
`;

const ControlDescription = styled.section`
	gap: var(--px-10);
	border-bottom: var(--px-1) solid var(--neutrals-200);
	padding: var(--px-4) var(--px-10) var(--px-16) var(--px-10);
	margin-bottom: var(--px-8);
`;

const StyledHeader = styled.section`
	display: flex;
	justify-content: space-between;

	.control-name-label {
		font-size: var(--px-16);
		color: var(--neutrals-700);
		font-weight: 400;
		line-height: var(--px-20);
	}
`;

const StyledControlName = styled.section`
	font-size: var(--px-16);
	color: var(--neutrals-900);
	font-weight: 400;
	line-height: var(--px-24);
`;

const ControlDetails = styled.section`
	gap: var(--px-8);
	> section {
		display: flex;
		justify-content: space-between;
		margin: var(--px-4);
	}
`;
